import { Link } from 'react-router-dom';
import Shell from '../components/Shell';
import { C2Button, C2Section, C2Shape } from '../components/c2';
import type { DecorShape } from '../components/c2/Section';
import DemoHero from '../components/demo/DemoHero';
import BeforeAfter from '../components/demo/BeforeAfter';
import InteractiveBrowserDemo from '../components/demo/InteractiveBrowserDemo';
import CreateLinkDemo from '../components/demo/CreateLinkDemo';

const interactiveDecor: DecorShape[] = [
  { shape: 'circle', size: 220, color: 'sky', style: { position: 'absolute', top: 60, left: -90, opacity: 0.4 } },
  { shape: 'pill', size: 240, color: 'peach', style: { position: 'absolute', top: 240, right: -80, opacity: 0.4, transform: 'rotate(8deg)' } },
  { shape: 'square', size: 130, color: 'lemon', style: { position: 'absolute', bottom: 80, left: '12%', opacity: 0.4, transform: 'rotate(-10deg)' } },
];

const createLinkDecor: DecorShape[] = [
  { shape: 'circle', size: 240, color: 'lilac', style: { position: 'absolute', top: 60, left: -100, opacity: 0.4 } },
  { shape: 'pill', size: 220, color: 'mint', style: { position: 'absolute', top: 220, right: -70, opacity: 0.4, transform: 'rotate(-8deg)' } },
  { shape: 'square', size: 130, color: 'peach', style: { position: 'absolute', bottom: 60, right: '12%', opacity: 0.4, transform: 'rotate(10deg)' } },
];

const beforeAfterDecor: DecorShape[] = [
  { shape: 'circle', size: 240, color: 'peach', style: { position: 'absolute', top: 80, left: -110, opacity: 0.4 } },
  { shape: 'pill', size: 220, color: 'sky', style: { position: 'absolute', top: 240, right: -70, opacity: 0.4, transform: 'rotate(8deg)' } },
  { shape: 'square', size: 140, color: 'mint', style: { position: 'absolute', bottom: 60, left: '15%', opacity: 0.4, transform: 'rotate(-10deg)' } },
  { shape: 'circle', size: 180, color: 'lemon', style: { position: 'absolute', bottom: 40, right: -50, opacity: 0.4 } },
];

export default function DemoPage() {
  return (
    <Shell>
      <section
        className="relative overflow-hidden"
        style={{ padding: '80px clamp(32px, 6vw, 96px) 64px' }}
      >
        <C2Shape shape="circle" size={260} color="peach" className="absolute opacity-[0.35] z-0" style={{ top: 40, right: -60 }} />
        <C2Shape shape="circle" size={120} color="mint" className="absolute opacity-40 z-0" style={{ bottom: 40, left: 60 }} />

        <div className="max-w-[1280px] mx-auto relative z-[1]">
          <div className="text-center mb-8">
            <h1
              className="font-display font-extrabold text-ink m-0 mx-auto mb-7"
              style={{
                fontSize: 'clamp(28px, 3.6vw, 62px)',
                letterSpacing: '-0.045em',
                lineHeight: 0.92,
                maxWidth: 720,
              }}
            >
              Watch a go/ link{' '}
              <span style={{ color: 'var(--accent-solid)' }}>go</span>
            </h1>
            <p
              className="text-ink-soft leading-[1.55] mx-auto"
              style={{ fontSize: 'clamp(17px, 1.6vw, 20px)', maxWidth: 640 }}
            >
              Go/ links are really quite simple. This page has a few demos for those who have never used them before.
            </p>
          </div>

          <DemoHero />
        </div>
      </section>

      <C2Section pad={96} bg="var(--color-bg-alt)" decor={interactiveDecor}>
        <InteractiveBrowserDemo />
      </C2Section>

      <C2Section pad={96} decor={createLinkDecor}>
        <CreateLinkDemo />
      </C2Section>

      <C2Section pad={96} decor={beforeAfterDecor}>
        <div className="text-center mb-12">
          <div
            className="inline-block uppercase font-semibold mb-4"
            style={{
              padding: '6px 14px',
              background: 'var(--accent-soft)',
              color: 'var(--accent-deep)',
              borderRadius: 999,
              fontSize: 12,
              letterSpacing: '0.02em',
            }}
          >
            The chaos problem
          </div>
          <h2
            className="font-display font-extrabold text-ink m-0 mx-auto mb-3.5"
            style={{
              fontSize: 'clamp(36px, 5vw, 56px)',
              letterSpacing: '-0.025em',
              lineHeight: 1.05,
              maxWidth: 760,
            }}
          >
            The same Slack thread, two different worlds.
          </h2>
          <p className="text-[17px] text-ink-soft mx-auto leading-[1.5]" style={{ maxWidth: 560 }}>
            Flip between "before" and "after" to see how a single go/ link quiets an entire channel.
          </p>
        </div>
        <BeforeAfter />
      </C2Section>

      <C2Section pad={96} bg="var(--color-bg-alt)" className="relative overflow-hidden">
        <C2Shape shape="circle" size={160} color="mint" className="absolute opacity-40" style={{ top: 60, left: '15%' }} />
        <C2Shape shape="circle" size={200} color="lemon" className="absolute opacity-40" style={{ bottom: 40, right: '10%' }} />
        <div className="max-w-[760px] mx-auto text-center relative z-[1]">
          <h2
            className="font-display font-extrabold text-ink m-0 mb-4"
            style={{
              fontSize: 'clamp(40px, 5.5vw, 64px)',
              letterSpacing: '-0.03em',
              lineHeight: 1.05,
            }}
          >
            Ready to stop hunting for links?
          </h2>
          <p className="text-lg text-ink-soft leading-[1.55] mx-auto mb-8" style={{ maxWidth: 520 }}>
            Takes 2 minutes to set up. Your team can start making go/ links the same day.
          </p>
          <div className="flex gap-3 justify-center flex-wrap">
            <Link to="/gatekeeper">
              <C2Button variant="accent" size="lg">Get started free</C2Button>
            </Link>
            <Link to="/">
              <C2Button variant="ghost" size="lg">← Back to overview</C2Button>
            </Link>
          </div>
        </div>
      </C2Section>
    </Shell>
  );
}
